import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { FieldsetModule } from 'primeng/fieldset';
import { UserService, RoleModel, UserTypeModel } from '../../services/user.service';
import { MessageService } from 'primeng/api';
import { Toast } from "primeng/toast";
import { AuthService } from '../../core/services/auth.service';

@Component({
  selector: 'app-inviteuser',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    FieldsetModule,
    Toast
  ],
  templateUrl: './inviteuser.html',
  styleUrl: './inviteuser.css',
})
export class InviteUser implements OnInit {
  private static readonly ROLE_SUPER_ADMIN = 1;
  private static readonly ROLE_ADMIN = 2;
  private static readonly ROLE_PROJECT_ADMIN = 3;
  private static readonly ROLE_USER = 4;
  private static readonly ADMIN_ONLY_USER_TYPES = new Set(['tmeic']);
  private static readonly STANDARD_USER_TYPES = new Set(['tmeic', 'asp', 'integrator', 'customer']);
  private static readonly ADMIN_ONLY_ROLES = new Set(['super admin', 'admin', 'project admin']);

  inviteUserForm!: FormGroup;
  isSubmitting: boolean = false;
  loadingOptions: boolean = false;
  projectId: number = 0;
  isEditMode: boolean = false;
  userId?: number;

  // Options loaded from API
  roleOptions: RoleModel[] = [];
  userTypeOptions: UserTypeModel[] = [];
  filteredUserTypeOptions: UserTypeModel[] = [];

  constructor(
    private fb: FormBuilder,
    public config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private userService: UserService,
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private authService: AuthService
  ) {
    this.initializeForm();
  }

  ngOnInit(): void {
    console.log('InviteUser dialog data:', this.config.data);
    this.projectId = this.config.data?.projectId || 0;
    this.isEditMode = this.config.data?.isEditMode || false;

    this.inviteUserForm.get('userType')?.disable({ emitEvent: false });
    this.handleRoleChanges();
    this.loadRolesAndUserTypes();

    // If in edit mode, populate the form with user data
    if (this.isEditMode && this.config.data?.user) {
      const user = this.config.data.user;
      this.userId = user.userId;

      this.inviteUserForm.patchValue({
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        role: user.roleId,
        userType: user.userTypeId,
        isActive: user.isActive ?? true,
        address1: user.address1 || '',
        address2: user.address2 || '',
        city: user.city || '',
        state: user.state || '',
        zip: user.zip || '',
        country: user.country || '',
        phoneNumber: user.phoneNumber || ''
      });

      // Disable email field in edit mode
      this.inviteUserForm.get('email')?.disable();
    }
  }

  private handleRoleChanges(): void {
    this.inviteUserForm.get('role')?.valueChanges.subscribe((roleId: number | string) => {
      this.updateFilteredUserTypes(Number(roleId) || 0);
    });
  }

  private updateFilteredUserTypes(roleId: number): void {
    const userTypeControl = this.inviteUserForm.get('userType');
    const selectedRole = this.roleOptions.find(role => role.roleId === roleId);
    const normalizedRoleName = (selectedRole?.roleName || '').trim().toLowerCase();

    if (!roleId || !selectedRole) {
      this.filteredUserTypeOptions = [];
      if (!this.isEditMode) {
        userTypeControl?.setValue('', { emitEvent: false });
      }
      userTypeControl?.disable({ emitEvent: false });
      return;
    }

    const allowedTypes = InviteUser.ADMIN_ONLY_ROLES.has(normalizedRoleName)
      ? InviteUser.ADMIN_ONLY_USER_TYPES
      : InviteUser.STANDARD_USER_TYPES;

    this.filteredUserTypeOptions = this.userTypeOptions.filter(userType =>
      allowedTypes.has((userType.name || '').trim().toLowerCase())
    );

    if (this.isEditMode) {
      const editingUserTypeId = Number(this.config.data?.user?.userTypeId) || 0;
      const existingUserType = this.userTypeOptions.find(userType => userType.userTypeId === editingUserTypeId);
      if (existingUserType && !this.filteredUserTypeOptions.some(userType => userType.userTypeId === editingUserTypeId)) {
        this.filteredUserTypeOptions = [existingUserType, ...this.filteredUserTypeOptions];
      }
    }

    userTypeControl?.enable({ emitEvent: false });

    const selectedUserTypeId = Number(userTypeControl?.value) || 0;
    const isCurrentTypeAllowed = this.filteredUserTypeOptions.some(userType => userType.userTypeId === selectedUserTypeId);
    if (selectedUserTypeId && !isCurrentTypeAllowed && !this.isEditMode) {
      userTypeControl?.setValue('', { emitEvent: false });
    }
  }

  private initializeForm(): void {
    this.inviteUserForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      firstName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      lastName: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      role: ['', Validators.required],
      userType: ['', Validators.required],
      isActive: [true],
      address1: ['', [Validators.maxLength(200)]],
      address2: ['', [Validators.maxLength(200)]],
      city: ['', [Validators.required, Validators.maxLength(100)]],
      state: ['', [Validators.required, Validators.maxLength(100)]],
      zip: ['', [Validators.maxLength(20)]],
      country: ['', [Validators.maxLength(100)]],
      phoneNumber: ['', [Validators.required, Validators.maxLength(20)]]
    });
  }

  loadRolesAndUserTypes(): void {
    this.loadingOptions = true;
    this.cdr.detectChanges();
    let rolesLoaded = false;
    let userTypesLoaded = false;
    
    const checkLoadingComplete = () => {
      if (rolesLoaded && userTypesLoaded) {
        this.loadingOptions = false;
        this.cdr.detectChanges();
      }
    };
    
    // Load roles
    this.userService.getRoles().subscribe({
      next: (roles: RoleModel[]) => {
        this.roleOptions = this.filterRoleOptionsByActor(roles);
        console.log('Loaded roles:', roles);
        console.log('Role options count:', this.roleOptions.length);
        this.updateFilteredUserTypes(Number(this.inviteUserForm.get('role')?.value) || 0);
        rolesLoaded = true;
        checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('Error loading roles:', error);
        this.roleOptions = [];
        rolesLoaded = true;
        checkLoadingComplete();
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to load roles' 
        });
      }
    });

    // Load user types
    this.userService.getUserTypes().subscribe({
      next: (userTypes: UserTypeModel[]) => {
        this.userTypeOptions = userTypes;
        console.log('Loaded user types:', userTypes);
        console.log('User type options count:', this.userTypeOptions.length);
        this.updateFilteredUserTypes(Number(this.inviteUserForm.get('role')?.value) || 0);
        userTypesLoaded = true;
        checkLoadingComplete();
      },
      error: (error: any) => {
        console.error('Error loading user types:', error);
        this.userTypeOptions = [];
        userTypesLoaded = true;
        checkLoadingComplete();
        this.messageService.add({ 
          severity: 'error', 
          summary: 'Error', 
          detail: 'Failed to load user types' 
        });
      }
    });
  }

  onSubmit(): void {
    if (this.inviteUserForm.invalid) {
      this.markFormGroupTouched(this.inviteUserForm);
      return;
    }

    this.isSubmitting = true;
    const formData = this.inviteUserForm.getRawValue(); // Use getRawValue to get disabled fields too

    if (this.isEditMode && this.userId) {
      this.proceedWithUpdate(formData);
    } else {
      this.proceedWithInvitation(formData);
    }
  }

  private proceedWithUpdate(formData: any): void {
    const updateRequest: any = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      roleId: formData.role,
      userTypeId: formData.userType,
      isActive: formData.isActive,
      address1: formData.address1,
      address2: formData.address2 || null,
      city: formData.city,
      state: formData.state,
      zip: formData.zip || null,
      country: formData.country || null,
      phoneNumber: formData.phoneNumber
    };

    console.log('Updating user:', this.userId, updateRequest);

    this.userService.editUser(this.userId!, updateRequest).subscribe({
      next: (response: any) => {
        console.log('User updated successfully:', response);
        this.ref.close({ success: true, data: response });
      },
      error: (error: any) => {
        console.error('Error updating user:', error);
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.getErrorMessage(error, 'Failed to update user')
        });
      }
    });
  }

  private proceedWithInvitation(formData: any): void {
    const inviteRequest: any = {
      email: formData.email,
      firstName: formData.firstName,
      lastName: formData.lastName,
      roleId: formData.role,
      userTypeId: formData.userType,
      projectId: this.projectId,
      address1: formData.address1,
      address2: formData.address2 || null,
      city: formData.city,
      state: formData.state,
      zip: formData.zip || null,
      country: formData.country || null,
      phoneNumber: formData.phoneNumber
    };

    console.log('Inviting user:', inviteRequest);

    this.userService.inviteUser(inviteRequest).subscribe({
      next: (response: any) => {
        console.log('User invited successfully:', response);
        this.ref.close({ success: true, data: response });
      },
      error: (error: any) => {
        console.error('Error inviting user:', error);
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.getErrorMessage(error, 'Failed to send invitation')
        });
      }
    });
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (typeof error === 'string') {
      return error;
    }

    return error?.error?.message || error?.message || fallback;
  }

  onCancel(): void {
    this.ref.close();
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach(key => {
      const control = formGroup.get(key);
      control?.markAsTouched();
    });
  }

  private filterRoleOptionsByActor(roles: RoleModel[]): RoleModel[] {
    if (this.isEditMode) {
      const editingRoleId = Number(this.config.data?.user?.roleId) || 0;
      return roles.filter(role =>
        role.roleId !== InviteUser.ROLE_SUPER_ADMIN ||
        editingRoleId === InviteUser.ROLE_SUPER_ADMIN
      );
    }

    const actorRoleId = Number(this.authService.getCurrentUser()?.roleId) || 0;
    const allowedRoleIds = this.getAllowedInvitedRoleIds(actorRoleId);
    return roles.filter(role => allowedRoleIds.has(role.roleId));
  }

  private getAllowedInvitedRoleIds(actorRoleId: number): Set<number> {
    switch (actorRoleId) {
      case InviteUser.ROLE_SUPER_ADMIN:
        return new Set([
          InviteUser.ROLE_ADMIN,
          InviteUser.ROLE_PROJECT_ADMIN,
          InviteUser.ROLE_USER
        ]);
      case InviteUser.ROLE_ADMIN:
        return new Set([
          InviteUser.ROLE_PROJECT_ADMIN,
          InviteUser.ROLE_USER
        ]);
      case InviteUser.ROLE_PROJECT_ADMIN:
      case InviteUser.ROLE_USER:
        return new Set([InviteUser.ROLE_USER]);
      default:
        return new Set<number>();
    }
  }

  // Getter for form controls
  get f() { return this.inviteUserForm.controls; }
}
